import { useEffect, useRef } from 'react';
import { useModalDrag } from '../hooks/useModalDrag';

/**
 * Modal dialog shown when leaving a topic chat with unsaved messages.
 * Props:
 *   onSave       — user chose "Save": persist messages then leave
 *   onDiscard    — user chose "Don't Save": leave without saving
 *   onCancel     — user chose "Continue": stay in the chat
 *   messageCount — number of unsaved messages (for display)
 */
export default function SaveOnLeaveDialog({ onSave, onDiscard, onCancel, messageCount }) {
  const saveRef = useRef(null);
  const { boxRef, onDragStart } = useModalDrag();

  // Focus the "Save" button when the dialog opens
  useEffect(() => {
    saveRef.current?.focus();
  }, []);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onCancel]);

  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget) onCancel();
  };

  return (
    <div className="modal-backdrop" onClick={handleBackdrop}>
      <div
        ref={boxRef}                              
        onMouseDown={onDragStart}                 
        className="modal-box save-on-leave-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sol-title"
      >
        {/* Icon */}
        <div className="sol-icon">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
            <polyline points="17 21 17 13 7 13 7 21" />
            <polyline points="7 3 7 8 15 8" />
          </svg>
        </div>

        {/* Heading */}
        <h2 className="modal-title" id="sol-title">Save conversation?</h2>

        {/* Description */}
        <p className="sol-description">
          You have{' '}
          <strong>{messageCount} unsaved message{messageCount !== 1 ? 's' : ''}</strong>{' '}
          in this topic. Would you like to save them to long-term memory before leaving?
        </p>

        {/* Actions */}
        <div className="sol-actions">
          <button
            ref={saveRef}
            className="sol-btn sol-btn-primary"
            onClick={onSave}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
              <polyline points="17 21 17 13 7 13 7 21" />
              <polyline points="7 3 7 8 15 8" />
            </svg>
            Save
          </button>

          <button
            className="sol-btn sol-btn-secondary"
            onClick={onDiscard}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              <path d="M10 11v6M14 11v6" />
            </svg>
            Don't Save
          </button>

          <button
            className="sol-btn sol-btn-ghost"
            onClick={onCancel}
          >
            Continue Chatting
          </button>
        </div>
      </div>
    </div>
  );
}
